import { useMutation } from "@apollo/client/react";
import {
  ContentBreadcrumbFragment_context,
  CreateGroupingQuestionAnswer,
  CreateGroupingQuestionAnswerVariables,
  GroupingCategoryAnswer,
  MultipleChoiceData,
} from "__generated__/types";
import classNames from "classnames";
import { Button } from "common/buttons";
import { MultilineText } from "common/components/MultilineText";
import { H2 } from "common/components/Typography";
import { useToggle } from "common/hooks/useToggle";
import { useFinishContent } from "domain/common/content-mutations";
import gql from "graphql-tag";
import React, { useState } from "react";
import styles from "./GroupingQuestion.module.css";
import { Result } from "./Result";

const GroupingQuestionAlternatives = React.lazy(() =>
  import("./GroupingQuestionAlternatives").then((it) => ({
    default: it.GroupingQuestionAlternatives,
  })),
);

const GroupingQuestionCategory = React.lazy(() =>
  import("./GroupingQuestionCategory").then((it) => ({
    default: it.GroupingQuestionCategory,
  })),
);

const MUTATION = gql`
  mutation CreateGroupingQuestionAnswer(
    $multipleChoiceId: ID!
    $groupingCategoryAnswers: [GroupingCategoryAnswer!]!
  ) {
    createMultipleChoiceAnswer(
      input: {
        multipleChoiceId: $multipleChoiceId
        groupingCategoryAnswers: $groupingCategoryAnswers
      }
    ) {
      multipleChoiceAnswer {
        id
        scoreInPercent
      }
    }
  }
`;

export interface QuestionState {
  selectedOptions: string[];
}

interface Props {
  multipleChoice: MultipleChoiceData;
  onFinishedElement?: () => void;
  isSyllabus?: boolean;
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;
}

export interface GroupingQuestionAlternativeItem {
  id: string;
  title: string;
}

export interface GroupingQuestionCategoryItem {
  id: string;
  title: string;
  content: string | undefined;
  selectedAlternatives: GroupingQuestionAlternativeItem[];
}

export const GroupingQuestion: React.FC<React.PropsWithChildren<Props>> = ({
  multipleChoice,
  onFinishedElement,
  isSyllabus,
  containerType,
}) => {
  const [delivered, setDelivered] = useState(false);
  const { handleFinishContent } = useFinishContent();

  const [mutate, { data }] = useMutation<
    CreateGroupingQuestionAnswer,
    CreateGroupingQuestionAnswerVariables
  >(MUTATION);

  const [showPreviousScore, setShowPreviousScore] = useToggle(
    !!multipleChoice.latestSubmitted,
  );
  const previousScore = multipleChoice.latestSubmitted?.scoreInPercent;
  const [categories, setCategories] = useState<GroupingQuestionCategoryItem[]>(
    multipleChoice.questions.map((e) => {
      return {
        id: e.id,
        title: e.title,
        content: e.body?.content,
        selectedAlternatives: [],
      };
    }),
  );

  const [alternatives, setAlternatives] = useState<
    GroupingQuestionAlternativeItem[]
  >(
    multipleChoice.options.map((e) => {
      return {
        id: e.id,
        title: e.title,
      };
    }),
  );

  function getGroupingCategoryAnswers(): GroupingCategoryAnswer[] {
    return categories.map((e) => ({
      questionId: e.id,
      optionIds: e.selectedAlternatives.map((e) => e.id),
    }));
  }

  const onSubmit = async () => {
    const result = await mutate({
      variables: {
        multipleChoiceId: multipleChoice.id,
        groupingCategoryAnswers: getGroupingCategoryAnswers(),
      },
    });

    if (result.data) {
      setDelivered(true);

      if (isSyllabus && containerType !== "SelfAssessment") {
        await handleFinishContent(multipleChoice.id);

        if (onFinishedElement) {
          onFinishedElement();
        }
      }
    }
  };

  function addAlternativeToCategory(
    alternative: GroupingQuestionAlternativeItem,
    category: GroupingQuestionCategoryItem,
  ) {
    const updatedCategories = categories.map((cat) => {
      if (cat.id === category.id) {
        const alternativeIds = cat.selectedAlternatives.map((alt) => alt.id);
        if (!alternativeIds.includes(alternative.id)) {
          return {
            ...cat,
            selectedAlternatives: [...cat.selectedAlternatives, alternative],
          };
        }
      } else {
        return {
          ...cat,
          selectedAlternatives: cat.selectedAlternatives.filter(
            (alt) => alt.id !== alternative.id,
          ),
        };
      }
      return cat;
    });

    const updatedAlternatives = alternatives.filter(
      (alt) => alt.id !== alternative.id,
    );

    setCategories(updatedCategories);
    setAlternatives(updatedAlternatives);
  }

  function addAlternativeToAlternatives(
    alternative: GroupingQuestionAlternativeItem,
  ) {
    const updatedCategories = categories.map((cat) => ({
      ...cat,
      selectedAlternatives: cat.selectedAlternatives.filter(
        (alt) => alt.id !== alternative.id,
      ),
    }));

    const alternativeIds = alternatives.map((alt) => alt.id);
    const updatedAlternatives = alternativeIds.includes(alternative.id)
      ? alternatives
      : [...alternatives, alternative];

    setCategories(updatedCategories);
    setAlternatives(updatedAlternatives);
  }

  function resetForm() {
    setDelivered(false);
    setCategories(
      multipleChoice.questions.map((e) => {
        return {
          id: e.id,
          title: e.title,
          content: e.body?.content,
          selectedAlternatives: [],
        };
      }),
    );
    setAlternatives(
      multipleChoice.options.map((e) => {
        return {
          id: e.id,
          title: e.title,
        };
      }),
    );
  }

  function deliver() {
    void onSubmit();
  }

  const score =
    data?.createMultipleChoiceAnswer.multipleChoiceAnswer.scoreInPercent;

  return (
    <div>
      <H2>{multipleChoice.title}</H2>
      {multipleChoice.ingress && (
        <MultilineText {...multipleChoice.ingress} openLinksSeparately />
      )}
      {delivered && score && typeof score === "number" ? (
        <Result score={score} resetForm={resetForm} />
      ) : showPreviousScore && typeof previousScore === "number" ? (
        <Result
          showNextElementButton={false}
          score={previousScore}
          summaryText="Sist gang fikk du"
          resetForm={() => {
            setShowPreviousScore();
          }}
        />
      ) : multipleChoice.questions.length == 0 ? (
        <p>
          Oisann! Denne oppgaven har ikke fått noen grupperingskategorier enda.
        </p>
      ) : (
        <div className={styles.content}>
          <div className={styles.groupingQuestion}>
            <div className={styles.groupingQuestionContent}>
              {categories.map((category) => (
                <GroupingQuestionCategory
                  title={category.title}
                  key={category.id}
                  content={category.content}
                  alternatives={category.selectedAlternatives}
                  onAdd={(e: GroupingQuestionAlternativeItem) => {
                    addAlternativeToCategory(e, category);
                  }}
                />
              ))}
            </div>
            <div>
              <GroupingQuestionAlternatives
                className={classNames(styles.groupingQuestionAlternatives)}
                alternatives={alternatives}
                onAdd={(e: GroupingQuestionAlternativeItem) => {
                  addAlternativeToAlternatives(e);
                }}
              />
            </div>
          </div>
          <div className={styles.buttonRow}>
            <Button
              onClick={() => {
                console.log("delivering");

                deliver();
              }}
            >
              Levér oppgave
            </Button>
            {delivered && <div>Oppgaven ble levert!</div>}
          </div>
        </div>
      )}
    </div>
  );
};
